import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFilters } from '../../context/FilterProvider';
import { formatDate } from '../../util/GenericUtil';
import { aggregateLabTests, normalizeAggregatedResults } from '../../util/GenericUtil';
import { formatDrawLabel } from '../../util/DrawGroupUtil';
import { parseDate } from '../../../lambda/shared-utils/bin-config.mjs';
import bloodGroupings from '../../../lambda/health-recommendations/blood_groupings.json';
import TestChartEntry from '../TestChartEntry/TestChartEntry';
import NewTestForm from '../NewTestForm/NewTestForm';
import Spinner from '../Spinner/Spinner';
import { OrganIcon } from '../OrganSystemCircle/OrganIcons';
import SystemIndex from '../SystemIndex/SystemIndex';
import deleteIcon from '../../assets/delete.svg';
import '../../styles/TestDetails.css';

function TestDetailsSection({
    bloodTestFiles,
    loadingFiles,
    normalizeTestName,
    editTests,
    newTest,
    setNewTest,
    updateEditTest,
    userFiles,
    setUserFiles,
    uploadingState,
    setUploadingState,
    patientId,
    userId,
    selectedTest,
    onTestClick,
    hideSystemFilter,
    scrollToSystem,
    onDelete,
    demo = false,
}) {
    const {
        selectedSystem: rawCategoryFilter, selectedFile, tags,
        selectSystem, selectFile, setTags,
        clearFilters: clearContextFilters,
    } = useFilters();

    // When hideSystemFilter is active (mobile), ignore the organ system filter entirely
    const categoryFilter = hideSystemFilter ? null : rawCategoryFilter;

    // searchQuery and dateRange are intentionally transient (local state, not in FilterContext).
    // They reset on navigation — these are page-specific filters that don't affect the dashboard.
    const [searchQuery, setSearchQuery] = useState('');
    const [dateRange, setDateRange] = useState('all');
    const [activeSystem, setActiveSystem] = useState(null);
    const [portalTarget, setPortalTarget] = useState(null);
    const systemRefs = useRef({});
    const pageRef = useRef(null);

    // Date-filtered files
    const dateFilteredFiles = useMemo(() => {
        if (!bloodTestFiles || dateRange === 'all') return bloodTestFiles;

        const now = new Date();
        let cutoff;
        switch (dateRange) {
            case '3m': cutoff = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()); break;
            case '6m': cutoff = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()); break;
            case '1y': cutoff = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()); break;
            default: return bloodTestFiles;
        }

        return bloodTestFiles.filter(file => {
            // testdate is a YYYYMMDD integer — new Date(20260902) reads it as epoch
            // milliseconds, so parse it explicitly.
            const testDate = parseDate(file.data?.header?.testdate);
            if (!testDate) return true;
            return testDate >= cutoff;
        });
    }, [bloodTestFiles, dateRange]);

    // Filtered tests (no alphabetical sort — ordering is per-group by JSON marker position)
    const filteredTests = useMemo(() => {
        if (!dateFilteredFiles) return [];

        let filtered = [];
        if (selectedFile === 'all') {
            dateFilteredFiles.forEach(file => {
                const testsWithDate = file.data.body.map(test => ({
                    ...test,
                    testdate: file.data.header.testdate
                }));
                filtered.push(...testsWithDate);
            });
            filtered = aggregateLabTests(filtered, normalizeTestName).map(normalizeAggregatedResults);
        } else if (typeof selectedFile === 'number' && dateFilteredFiles[selectedFile]) {
            filtered = dateFilteredFiles[selectedFile]?.data?.body || [];
        }

        if (categoryFilter && bloodGroupings[categoryFilter]) {
            const systemMarkers = Object.keys(bloodGroupings[categoryFilter].markers);
            filtered = filtered.filter(test => {
                const testName = test.alias || test.test_name;
                return systemMarkers.includes(testName);
            });
        }

        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            filtered = filtered.filter(test => {
                const name = (test.alias || test.test_name || '').toLowerCase();
                return name.includes(q);
            });
        }

        return filtered;
    }, [dateFilteredFiles, selectedFile, normalizeTestName, categoryFilter, searchQuery]);

    const isSearchActive = searchQuery.trim().length > 0;
    const [flatMode, setFlatMode] = useState(false);
    const isFlatView = isSearchActive || flatMode;

    // Group tests by organ system, ordered by JSON marker position (only when not in flat view)
    const systemGroupedTests = useMemo(() => {
        if (isFlatView) return {};

        const groups = {};
        const systemKeys = categoryFilter
            ? [categoryFilter]
            : Object.keys(bloodGroupings);

        // Build a lookup from filtered tests for O(1) matching
        const testsByName = {};
        filteredTests.forEach(test => {
            const testName = test.alias || test.test_name;
            if (!testsByName[testName]) testsByName[testName] = test;
        });

        const placed = new Set();

        systemKeys.forEach(systemKey => {
            const system = bloodGroupings[systemKey];
            if (!system) return;
            const systemMarkers = Object.keys(system.markers);
            const orderedTests = systemMarkers
                .filter(marker => marker in testsByName)
                .map(marker => {
                    placed.add(marker);
                    return testsByName[marker];
                });
            if (orderedTests.length > 0) {
                groups[systemKey] = orderedTests;
            }
        });

        if (!categoryFilter) {
            const other = filteredTests.filter(test => {
                const testName = test.alias || test.test_name;
                return !placed.has(testName);
            });
            if (other.length > 0) {
                groups._other = other;
            }
        }

        return groups;
    }, [filteredTests, categoryFilter, isFlatView]);

    // Flat sorted list for flat view mode, grouped by first letter
    const flatTests = useMemo(() => {
        if (!isFlatView) return [];
        return [...filteredTests].sort((a, b) => {
            const nameA = (a.alias || a.test_name || '').toLowerCase();
            const nameB = (b.alias || b.test_name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }, [filteredTests, isFlatView]);

    const letterGroupedTests = useMemo(() => {
        if (!isFlatView || flatTests.length === 0) return {};
        const groups = {};
        flatTests.forEach(test => {
            const name = (test.alias || test.test_name || '').toUpperCase();
            const letter = name[0] || '#';
            if (!groups[letter]) groups[letter] = [];
            groups[letter].push(test);
        });
        return groups;
    }, [flatTests, isFlatView]);

    // Available systems for SystemIndex sidebar
    const availableSystems = useMemo(() => {
        if (isFlatView) {
            // Alphabetical letter index with SYS toggle at top
            const letters = Object.keys(letterGroupedTests);
            return [
                { key: '_sys', icon: 'sys' },
                ...letters.map(l => ({ key: `letter_${l}`, icon: l })),
            ];
        }
        const organSystems = Object.keys(bloodGroupings)
            .filter(key => key in systemGroupedTests)
            .map(key => ({ key, icon: bloodGroupings[key].icon }));
        const result = [{ key: 'all', icon: 'all' }, ...organSystems];
        if ('_other' in systemGroupedTests) {
            result.push({ key: '_other', icon: 'other' });
        }
        return result;
    }, [systemGroupedTests, letterGroupedTests, isFlatView]);

    // Find portal target for sidebar (render outside scroll flow).
    // On mobile, .vpage-tests sits above the transform chain (.tab-slider has
    // will-change:transform which breaks position:fixed), so we portal there.
    useEffect(() => {
        const target = pageRef.current?.closest('.page-tests')
            || pageRef.current?.closest('.vpage-tests');
        if (target) setPortalTarget(target);
    }, []);

    // IntersectionObserver for active system tracking
    useEffect(() => {
        if (availableSystems.length === 0 || !pageRef.current) return;
        const scrollRoot = pageRef.current.closest('.page-tests-scroll') || pageRef.current;

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        const system = entry.target.dataset.system;
                        setActiveSystem(system);
                        break;
                    }
                }
            },
            { root: scrollRoot, rootMargin: '-10px 0px -70% 0px', threshold: 0 }
        );

        Object.values(systemRefs.current).forEach(el => {
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [availableSystems, systemGroupedTests]);

    // Auto-scroll to a specific organ system section (used by mobile PWA mode)
    useEffect(() => {
        if (!scrollToSystem || isFlatView) return;
        // Wait for grouped content to render, then jump to the target system instantly
        requestAnimationFrame(() => {
            const el = systemRefs.current[scrollToSystem];
            if (el) {
                const scrollContainer = pageRef.current?.closest('.page-tests-scroll') || pageRef.current?.closest('.page-tests');
                if (scrollContainer) {
                    const containerRect = scrollContainer.getBoundingClientRect();
                    const elRect = el.getBoundingClientRect();
                    scrollContainer.scrollTo({
                        top: scrollContainer.scrollTop + (elRect.top - containerRect.top),
                        behavior: 'instant',
                    });
                }
                setActiveSystem(scrollToSystem);
            }
        });
    }, [scrollToSystem, systemGroupedTests, isFlatView]);

    const scrollToRef = useCallback((key, behavior = 'smooth') => {
        requestAnimationFrame(() => {
            const el = systemRefs.current[key];
            if (el) {
                const scrollContainer = pageRef.current?.closest('.page-tests-scroll') || pageRef.current?.closest('.page-tests');
                if (scrollContainer) {
                    const containerRect = scrollContainer.getBoundingClientRect();
                    const elRect = el.getBoundingClientRect();
                    scrollContainer.scrollTo({
                        top: scrollContainer.scrollTop + (elRect.top - containerRect.top),
                        behavior,
                    });
                }
                setActiveSystem(key);
            }
        });
    }, []);

    const handleSystemSelect = useCallback((systemKey) => {
        // Toggle back to organ system view
        if (systemKey === '_sys') {
            setFlatMode(false);
            setActiveSystem(null);
            const scrollContainer = pageRef.current?.closest('.page-tests-scroll') || pageRef.current?.closest('.page-tests');
            scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        // Toggle to flat alphabetical view
        if (systemKey === 'all') {
            setFlatMode(true);
            setActiveSystem('all');
            const scrollContainer = pageRef.current?.closest('.page-tests-scroll') || pageRef.current?.closest('.page-tests');
            scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        // Scroll to letter group in flat view
        if (systemKey.startsWith('letter_')) {
            scrollToRef(systemKey);
            return;
        }
        // Scroll to organ system group
        setFlatMode(false);
        scrollToRef(systemKey);
    }, [scrollToRef]);

    const clearFilters = () => {
        clearContextFilters();
        setSearchQuery('');
        setDateRange('all');
    };

    const handleUpdateEditTest = (newValue) => {
        if (updateEditTest) updateEditTest(newValue);
    };

    const renderContent = () => {
        if (loadingFiles) {
            return (
                <div className="test-details-loading">
                    <Spinner size={32} color="#666" />
                    <p>Loading tests...</p>
                </div>
            );
        }

        if (selectedFile === 'new' && newTest) {
            return (
                <NewTestForm
                    setSelectedFile={selectFile}
                    normalizeTestName={normalizeTestName}
                    newTest={newTest}
                    setNewTest={setNewTest}
                    edit={false}
                    userFiles={userFiles}
                    setUserFiles={setUserFiles}
                    setUploadingState={setUploadingState}
                    patientId={patientId}
                    userId={userId}
                />
            );
        }

        if (typeof selectedFile === 'number' && editTests && dateFilteredFiles?.[selectedFile]?.key in editTests) {
            return (
                <NewTestForm
                    setSelectedFile={selectFile}
                    normalizeTestName={normalizeTestName}
                    newTest={editTests[dateFilteredFiles[selectedFile].key]}
                    setNewTest={handleUpdateEditTest}
                    edit={true}
                    userFiles={userFiles}
                    setUserFiles={setUserFiles}
                    setUploadingState={setUploadingState}
                    patientId={patientId}
                    userId={userId}
                />
            );
        }

        if (!filteredTests.length) {
            return (
                <div className="test-details-empty">
                    <p>No tests found</p>
                    {((!hideSystemFilter && categoryFilter) || searchQuery) && (
                        <button onClick={clearFilters} className="clear-filters-btn">
                            Clear Filters
                        </button>
                    )}
                </div>
            );
        }

        // Flat view (search or "All" selected): grouped by first letter
        if (isFlatView) {
            return Object.entries(letterGroupedTests).map(([letter, tests]) => {
                const refKey = `letter_${letter}`;
                return (
                    <div key={refKey} className="organ-system-group"
                         ref={el => systemRefs.current[refKey] = el}
                         data-system={refKey}>
                        <div className="organ-system-group-header">
                            <span>{letter}</span>
                        </div>
                        {tests.map((test, i) => (
                            <TestChartEntry
                                key={`flat-${letter}-${i}-${selectedFile}`}
                                test={test}
                                tags={tags}
                                aggregate={selectedFile === 'all'}
                                onTestClick={selectedFile === 'all' && onTestClick ? () => onTestClick(test, 'flat') : undefined}
                                isSelected={selectedFile === 'all' && selectedTest?.test_name === test.test_name && selectedTest?._groupKey === 'flat'}
                            />
                        ))}
                    </div>
                );
            });
        }

        // Default: organ system grouped view
        return Object.entries(systemGroupedTests).map(([systemKey, tests]) => {
            const isOther = systemKey === '_other';
            const system = isOther ? null : bloodGroupings[systemKey];
            return (
                <div key={systemKey} className="organ-system-group"
                     ref={el => systemRefs.current[systemKey] = el}
                     data-system={systemKey}>
                    <div className="organ-system-group-header">
                        {isOther ? (
                            <span>Other</span>
                        ) : (
                            <>
                                <OrganIcon icon={system.icon} />
                                <span>{system.display_name}</span>
                            </>
                        )}
                    </div>
                    {tests.map((test, i) => (
                        <TestChartEntry
                            key={`${systemKey}-${i}-${selectedFile}`}
                            test={test}
                            tags={tags}
                            aggregate={selectedFile === 'all'}
                            onTestClick={selectedFile === 'all' && onTestClick ? () => onTestClick(test, systemKey) : undefined}
                            isSelected={selectedFile === 'all' && selectedTest?.test_name === test.test_name && selectedTest?._groupKey === systemKey}
                        />
                    ))}
                </div>
            );
        });
    };

    const hasActiveFilters = tags.length > 0 || categoryFilter || searchQuery.trim() || dateRange !== 'all';

    const activeFilterCount = [
        dateRange !== 'all',
        tags.length > 0,
        categoryFilter != null,
        searchQuery.trim().length > 0,
    ].filter(Boolean).length;

    return (
        <div className="test-details-page" ref={pageRef}>
            <div className="test-details-tabs">
                {dateFilteredFiles?.length > 1 && (
                    <button
                        className={`tab-pill ${selectedFile === 'all' ? 'selected' : ''}`}
                        onClick={() => selectFile('all')}
                    >
                        All Tests
                    </button>
                )}
                {dateFilteredFiles?.map((file, i) => (
                    <button
                        key={i}
                        className={`tab-pill ${selectedFile === i ? 'selected' : ''}`}
                        onClick={() => selectFile(i)}
                    >
                        {formatDrawLabel(file, formatDate)}
                    </button>
                ))}
                {newTest && (
                    <button
                        className={`tab-pill new-test ${selectedFile === 'new' ? 'selected' : ''}`}
                        onClick={() => selectFile('new')}
                    >
                        New Test
                    </button>
                )}
            </div>

            {selectedFile !== 'new' && (
                <>
                    {!hideSystemFilter && (
                        <div className="system-icon-filters">
                            {Object.entries(bloodGroupings).map(([key, system]) => (
                                <button
                                    key={key}
                                    className={`system-icon-btn ${categoryFilter === key ? 'active' : ''}`}
                                    onClick={() => selectSystem(key)}
                                >
                                    <OrganIcon icon={system.icon} />
                                    <span>{system.display_name}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="test-details-search">
                        <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" />
                            <path d="M21 21l-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Search biomarkers..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                        {searchQuery && (
                            <button className="search-clear" onClick={() => setSearchQuery('')}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>

                    <div className="combined-filters">
                        <div className="filter-scroll-row">
                            {[
                                { value: 'all', label: 'All' },
                                { value: '3m', label: '3M' },
                                { value: '6m', label: '6M' },
                                { value: '1y', label: '1Y' }
                            ].map(opt => (
                                <button
                                    key={opt.value}
                                    className={`filter-chip date ${dateRange === opt.value ? 'active' : ''}`}
                                    onClick={() => setDateRange(opt.value)}
                                >
                                    {opt.label}
                                </button>
                            ))}

                            <span className="filter-divider" />

                            <button
                                className={`filter-chip status-chip optimal ${tags.includes('optimal') ? '' : 'inactive'}`}
                                onClick={() => setTags(tags.includes('optimal')
                                    ? tags.filter(t => t !== 'optimal')
                                    : [...tags, 'optimal'])}
                            >
                                Optimal
                            </button>
                            <button
                                className={`filter-chip status-chip dysfunctional ${
                                    tags.includes('elevated') || tags.includes('diminished') ? '' : 'inactive'
                                }`}
                                onClick={() => {
                                    const newTags = new Set(tags);
                                    if (newTags.has('elevated') || newTags.has('diminished')) {
                                        newTags.delete('elevated');
                                        newTags.delete('diminished');
                                    } else {
                                        newTags.add('elevated');
                                        newTags.add('diminished');
                                    }
                                    setTags([...newTags]);
                                }}
                            >
                                Dysfunctional
                            </button>
                            <button
                                className={`filter-chip status-chip abnormal ${
                                    tags.includes('high') || tags.includes('low') ? '' : 'inactive'
                                }`}
                                onClick={() => {
                                    const newTags = new Set(tags);
                                    if (newTags.has('high') || newTags.has('low')) {
                                        newTags.delete('high');
                                        newTags.delete('low');
                                    } else {
                                        newTags.add('high');
                                        newTags.add('low');
                                    }
                                    setTags([...newTags]);
                                }}
                            >
                                Abnormal
                            </button>

                            {hasActiveFilters && (
                                <button className="clear-all-btn" onClick={clearFilters}>
                                    Clear ({activeFilterCount})
                                </button>
                            )}

                            {onDelete && selectedFile !== 'all' && typeof selectedFile === 'number' && (
                                <>
                                    <span className="filter-divider" />
                                    <button
                                        className="filter-delete-btn"
                                        disabled={demo}
                                        title={demo ? "Demo — not available" : "Delete test"}
                                        onClick={onDelete}
                                    >
                                        <img src={deleteIcon} alt="Delete" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}

            <div className="test-details-list">
                {renderContent()}
            </div>

            {selectedFile !== 'new' && availableSystems.length > 0 && (
                portalTarget ? createPortal(
                    <SystemIndex
                        systems={availableSystems}
                        activeSystem={activeSystem}
                        onSystemSelect={handleSystemSelect}
                    />,
                    portalTarget
                ) : (
                    <SystemIndex
                        systems={availableSystems}
                        activeSystem={activeSystem}
                        onSystemSelect={handleSystemSelect}
                    />
                )
            )}
        </div>
    );
}

export default TestDetailsSection;
